'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import MaterialButton from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { excludeComponentProps } from '../utils/excludeComponentProps';
const StyledButton = styled(MaterialButton, {
    ...excludeComponentProps('subNavigationItems')
})(({ theme, variant }) => ({
    transition: 'unset',
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    ...variant === 'primary' && {
        backgroundColor: theme.tokens.FuelColorBackgroundActionPrimaryBase,
        color: theme.tokens.FuelColorTextActionPrimary,
        boxShadow: `${theme.tokens.FuelShadowXs}, ${theme.tokens.FuelShadowInsetButton}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`,
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionPrimaryHovered,
            boxShadow: `${theme.tokens.FuelShadowXs}, ${theme.tokens.FuelShadowInsetButton}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`
        },
        '&:active': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionPrimaryPressed,
            boxShadow: `${theme.tokens.FuelShadowXs}, ${theme.tokens.FuelShadowInsetButton}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`
        }
    },
    ...variant === 'secondary' && {
        backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryBase,
        color: theme.tokens.FuelColorTextActionSecondary,
        boxShadow: `${theme.tokens.FuelShadowXs}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`,
        border: `1px solid ${theme.tokens.FuelColorBorderDark}`,
        borderTopColor: '#bfbfbf',
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryHovered,
            borderColor: theme.tokens.FuelColorBorderDark,
            boxShadow: `${theme.tokens.FuelShadowXs}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`
        },
        '&:active': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryPressed,
            borderColor: theme.tokens.FuelColorBorderDark,
            boxShadow: `${theme.tokens.FuelShadowXs}, rgba(0, 0, 0, 0.05) 0px 1px 0px 0px`
        }
    },
    ...variant === 'text' && {
        backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
        color: theme.tokens.FuelColorTextActionTertiary,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryHovered,
            boxShadow: 'none'
        },
        '&:active': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryPressed,
            boxShadow: 'none'
        }
    },
    ...variant === 'text-inline' && {
        backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
        color: theme.tokens.FuelColorTextActionTertiary,
        textDecoration: 'underline',
        borderRadius: '0',
        boxShadow: 'none',
        fontWeight: 'normal',
        padding: '0',
        minWidth: 'auto',
        verticalAlign: 'baseline',
        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
            color: theme.tokens.FuelColorTextLinkHovered,
            textDecoration: 'underline',
            boxShadow: 'none'
        },
        '&:active': {
            boxShadow: 'none'
        }
    },
    '&:focus': {
        outline: 'none'
    },
    '&.Mui-focusVisible': {
        boxShadow: theme.tokens.FuelShadowFocused,
        ...variant === 'primary' && {
            boxShadow: `${theme.tokens.FuelShadowInsetButton}, ${theme.tokens.FuelShadowFocused}`
        },
        ...variant === 'secondary' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryBase
        },
        ...variant === 'text' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase
        },
        ...variant === 'text-inline' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
            color: theme.tokens.FuelColorTextActionTertiary
        }
    },
    '&.MuiButton-sizeSmall': {
        height: '2rem',
        padding: '3px 9px',
        ...variant === 'primary' && {
            padding: '4px 10px'
        },
        ...variant === 'text' && {
            padding: '4px 5px'
        },
        ...variant === 'text-inline' && {
            height: 'auto',
            padding: 0
        }
    },
    '&.MuiButton-sizeMedium': {
        height: '2.25rem',
        padding: '5px 15px',
        ...variant === 'primary' && {
            padding: '6px 16px'
        },
        ...variant === 'text' && {
            padding: '6px 8px'
        },
        ...variant === 'text-inline' && {
            height: 'auto',
            padding: 0
        }
    },
    '&.MuiButton-sizeLarge': {
        height: '2.5rem',
        padding: '7px 22px',
        fontSize: theme.tokens.FuelFontSizeBase,
        ...variant === 'primary' && {
            padding: '8px 22px'
        },
        ...variant === 'text' && {
            padding: '8px 11px'
        },
        ...variant === 'text-inline' && {
            height: 'auto',
            padding: 0
        }
    },
    '& .MuiButton-startIcon': {
        marginRight: theme.tokens.FuelSpacing1,
        ...variant === 'text-inline' && {
            marginRight: 0
        }
    },
    '& .MuiButton-endIcon': {
        marginLeft: theme.tokens.FuelSpacing1,
        ...variant === 'text-inline' && {
            marginLeft: 0
        }
    },
    '&[disabled], &.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
        ...variant === 'primary' && {
            backgroundColor: `${theme.tokens.FuelColorBackgroundActionPrimaryBase}`,
            color: theme.tokens.FuelColorTextActionPrimary,
            boxShadow: 'none',
            opacity: '.5'
        },
        ...variant === 'secondary' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionSecondaryBase,
            color: theme.tokens.FuelColorTextActionSecondary,
            borderColor: theme.tokens.FuelColorBorderLight,
            boxShadow: 'none',
            opacity: '.5'
        },
        ...variant === 'text' && {
            backgroundColor: theme.tokens.FuelColorBackgroundActionTertiaryBase,
            color: theme.tokens.FuelColorTextActionTertiary,
            opacity: '.5'
        },
        ...variant === 'text-inline' && {
            color: theme.tokens.FuelColorTextActionTertiary,
            opacity: '.5'
        }
    }
}));
export const Button = React.forwardRef(({ variant = 'secondary', size = 'medium', ...props }, ref) => (_jsx(StyledButton, { disableElevation: true, disableRipple: true, ref: ref, size: size, variant: variant, ...props })));
Button.displayName = 'Button';
